/**
 * iter326 — Raccourcis du tableau de bord, pensés pour le pouce.
 *
 * Les tuiles « Photo » et « Justificatif » déclenchent directement l'appareil
 * photo du téléphone : la photo part en arrière-plan, sans passer par la page.
 * Les autres ouvrent la saisie correspondante en un geste.
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Receipt, UserPlus, Wrench, CalendarPlus, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { photoCompress } from "@/components/or/photoCompress";

function Tuile({ icon: Icon, label, hint, accent, onClick, busy, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="group relative flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-2 py-4 min-h-[96px] text-center shadow-sm transition-transform transition-shadow duration-150 hover:shadow-md active:scale-[0.97] disabled:opacity-60 disabled:active:scale-100"
    >
      <span className={`flex h-11 w-11 items-center justify-center rounded-full ${accent}`}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
      </span>
      <span className="text-xs font-semibold leading-tight text-gray-900">{label}</span>
      {hint ? <span className="hidden sm:block text-[10px] text-gray-400">{hint}</span> : null}
    </button>
  );
}

export default function RaccourcisRapides() {
  const navigate = useNavigate();
  const photoRef = useRef(null);
  const ticketRef = useRef(null);
  const [envoiPhoto, setEnvoiPhoto] = useState(false);
  const [envoiTicket, setEnvoiTicket] = useState(false);

  const envoyerPhotos = async (e) => {
    const fichiers = Array.from(e.target.files || []);
    if (!fichiers.length) return;
    setEnvoiPhoto(true);
    try {
      const photos = [];
      for (const f of fichiers) photos.push(await photoCompress(f, 600));
      await api.post("/photos", { photos, vehicule_id: null });
      toast.success(`${photos.length} photo(s) enregistrée(s) · véhicule à préciser`, {
        action: { label: "Voir", onClick: () => navigate("/photos") },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi de la photo impossible");
    } finally {
      setEnvoiPhoto(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const envoyerTicket = async (e) => {
    const fichiers = Array.from(e.target.files || []);
    if (!fichiers.length) return;
    setEnvoiTicket(true);
    try {
      const fd = new FormData();
      fichiers.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/depenses-rapides/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Justificatif enregistré — ${data.reference_interne}. Lecture en cours…`);
      try {
        await api.post(`/depenses-rapides/${data.id}/ocr`);
      } catch {
        toast.error("Lecture automatique échouée — à compléter à la main");
      }
      navigate(`/depenses-rapides/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi du justificatif impossible");
    } finally {
      setEnvoiTicket(false);
      if (ticketRef.current) ticketRef.current.value = "";
    }
  };

  return (
    <div className="mb-6" data-testid="dashboard-raccourcis">
      <input
        ref={photoRef} type="file" multiple accept="image/*" capture="environment"
        className="hidden" onChange={envoyerPhotos} data-testid="raccourci-photo-input"
      />
      <input
        ref={ticketRef} type="file" multiple accept="image/*" capture="environment"
        className="hidden" onChange={envoyerTicket} data-testid="raccourci-ticket-input"
      />
      <div className="grid grid-cols-3 gap-2.5 sm:gap-3 lg:grid-cols-6">
        <Tuile
          icon={Camera} label="Photo" hint="atelier"
          accent="bg-[#52a675]/10 text-[#52a675]"
          busy={envoiPhoto} onClick={() => photoRef.current?.click()}
          testId="raccourci-photo"
        />
        <Tuile
          icon={Receipt} label="Justificatif" hint="dépense"
          accent="bg-orange-50 text-orange-600"
          busy={envoiTicket} onClick={() => ticketRef.current?.click()}
          testId="raccourci-justificatif"
        />
        <Tuile
          icon={UserPlus} label="Client" hint="nouveau"
          accent="bg-blue-50 text-blue-600"
          onClick={() => navigate("/clients/nouveau")}
          testId="raccourci-client"
        />
        <Tuile
          icon={Wrench} label="Nouvel OR" hint="atelier"
          accent="bg-purple-50 text-purple-600"
          onClick={() => navigate("/atelier/or/nouveau")}
          testId="raccourci-or"
        />
        <Tuile
          icon={CalendarPlus} label="Rendez-vous" hint="planifier"
          accent="bg-amber-50 text-amber-600"
          onClick={() => navigate("/calendrier?nouveau=1")}
          testId="raccourci-rdv"
        />
        <Tuile
          icon={Search} label="Plaque" hint="rechercher"
          accent="bg-gray-100 text-gray-600"
          onClick={() => window.dispatchEvent(new CustomEvent("crm:ouvrir-recherche"))}
          testId="raccourci-recherche"
        />
      </div>
    </div>
  );
}
